import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserProfile, useSignIn, SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { Button } from '../components/ui/button';

const ManageAccountPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { signIn, setActive } = useSignIn();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Handle Clerk sign-in token from mobile app
  useEffect(() => {
    const ticket = searchParams.get('__clerk_ticket');
    
    if (ticket && signIn) {
      setIsAuthenticating(true);
      
      signIn.create({ 
        strategy: 'ticket',
        ticket: ticket 
      })
      .then((result) => {
        if (result.status === 'complete') {
          setActive({ session: result.createdSessionId });
          // Remove ticket from URL
          searchParams.delete('__clerk_ticket');
          setSearchParams(searchParams);
        }
      })
      .catch((err) => {
        console.error('Sign-in error:', err);
      })
      .finally(() => {
        setIsAuthenticating(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps intentional - only run once on mount

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {isAuthenticating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-saverly-primary"></div>
              <p className="text-gray-700">Signing you in...</p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-8 pb-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-3">
            Manage Your{' '}
            <span className="text-saverly-primary">Account</span>
          </h1>
          <p className="text-lg text-gray-600 font-inter">
            Update your profile, billing, and subscription settings
          </p>
        </div>
      </section>

      {/* User Profile Component - Shows for signed-in users */}
      <SignedIn>
        <section className="py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <UserProfile 
              appearance={{
                elements: {
                  rootBox: "mx-auto",
                  card: "shadow-lg",
                }
              }}
            />
          </div>
        </section>
      </SignedIn>

      {/* Sign In Prompt for non-authenticated users */}
      <SignedOut>
        <section className="py-8 px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-10 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-2xl mx-auto flex items-center justify-center">
                  <svg className="w-8 h-8 text-saverly-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-3">
                Sign In to Continue
              </h2>
              <p className="text-gray-600 mb-8 font-inter">
                Please sign in to your Saverly account to manage your settings.
              </p>
              
              <SignInButton mode="modal">
                <Button className="w-full bg-saverly-primary hover:bg-saverly-deep text-white px-8 py-3 rounded-xl font-semibold shadow-sm transition-all">
                  Sign In to Your Account
                </Button>
              </SignInButton>
            </div>
          </div>
        </section>
      </SignedOut>
    </div>
  );
};

export default ManageAccountPage;

